import { motion } from 'framer-motion';
import { cn } from '../lib/cn';
import { springDefault } from '../tokens';

type Tone = 'accent' | 'positive' | 'negative' | 'warning';

type ProgressBarProps = {
  /** 0..1 — values outside this range are clamped. */
  value: number;
  tone?: Tone;
  className?: string;
  ariaLabel?: string;
};

const fillClass: Record<Tone, string> = {
  accent: 'bg-accent',
  positive: 'bg-positive',
  negative: 'bg-negative',
  warning: 'bg-warning',
};

// Hex colors paralleling fillClass, used for the soft glow on the fill
// end. Tailwind's CSS variables can't drive a box-shadow color directly
// without arbitrary-value gymnastics, so this lookup is the cleanest.
const glowRgb: Record<Tone, string> = {
  accent: '0, 122, 255',
  positive: '52, 199, 89',
  negative: '255, 59, 48',
  warning: '255, 149, 0',
};

export function ProgressBar({
  value,
  tone = 'accent',
  className,
  ariaLabel,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const atGoal = clamped >= 0.999;
  return (
    <div
      className={cn(
        'w-full h-[5px] rounded-pill bg-surface-secondary overflow-hidden',
        atGoal && 'breathing',
        className,
      )}
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
    >
      <motion.div
        className={cn('h-full rounded-pill', fillClass[tone])}
        initial={false}
        animate={{ width: `${clamped * 100}%` }}
        transition={springDefault}
        style={{
          boxShadow:
            clamped > 0
              ? `0 0 8px rgba(${glowRgb[tone]}, 0.35)`
              : 'none',
        }}
      />
    </div>
  );
}
